import type { SettingsFlow, UpdateSettingsFlowBody } from "@ory/kratos-client";
import type { AxiosError } from "axios";
import type React from "react";
import { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
	ContentContainer,
	ContentContainerWrapper,
} from "~/components/ContentContainer";
import { handleFlowError } from "~/components/KratosFlow";
import { DefaultLayout } from "~/components/Layout";
import { Loading } from "~/components/Loading";
import { ModalAction, ModalContent } from "~/components/Modal";
import { useModal } from "~/components/Modal/useModal";
import { useSnackBar } from "~/components/SnackBar";
import { kratos, SessionContext } from "~/ory";
import { MfaSettings } from "~/pages/Settings/MfaSettings";
import { PasswordSettings } from "~/pages/Settings/PasswordSettings";
import { ProfileSettings } from "~/pages/Settings/ProfileSettings";
import { useRegistrationReturnTo } from "~/store";
import { createFlowErrorHandler } from "~/utils/errorHandler";
import { ReactivateEmailDeliveryStatusSettings } from "./ReactivateEmailDeliveryStatusSettings";

const methodAll = "all";
const mfaMethods = ["mfa", "webauthn", "totp", "lookup_secret"];

export const Settings: React.FC = () => {
	const session = useContext(SessionContext);
	const [
		registrationReturnTo,
		_setRegistrationReturnTo, // eslint-disable-line  @typescript-eslint/no-unused-vars
		removeRegistrationReturnTo,
	] = useRegistrationReturnTo();
	const [openModal, closeModal] = useModal();
	const [openSnackBar] = useSnackBar();
	const [flow, setFlow] = useState<SettingsFlow | undefined>();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const paramMethod = searchParams.get("method");
	const [method, setMethod] = useState<string>(paramMethod ?? methodAll);

	// Get ?flow=... from the URL
	const returnTo = searchParams.get("return_to");
	const flowId = searchParams.get("flow");

	// In this effect we either initiate a new settings flow, or we fetch an existing settings flow.
	// biome-ignore lint/correctness/useExhaustiveDependencies: openSnackBar/openModal/closeModal are intentionally excluded. They are used only for fire-and-forget error display, and this effect should re-fetch on session/flow/URL changes only. They are stable in production (SnackBar/Modal providers memoize open/close via useCallback), so excluding them changes nothing at runtime; biome just cannot verify that stability across the context boundary.
	useEffect(() => {
		if (!session) return;
		// If the router is not ready yet, or we already have a flow, do nothing.
		if (flow) {
			return;
		}

		// Fetching/initializing a flow can fail at the network level (request aborted,
		// offline) with no HTTP response. handleFlowError can't classify those and re-rejects,
		// so we add a terminal handler to avoid an unhandled promise rejection (mirrors submit()).
		const onFlowError = createFlowErrorHandler(
			openSnackBar,
			openModal,
			closeModal,
		);

		// If ?flow=.. was in the URL, we fetch it
		if (flowId) {
			kratos
				.getSettingsFlow({ id: String(flowId) })
				.then(({ data }) => {
					// We received the flow - let's use its data and render the form!
					setFlow(data);
				})
				.catch(handleFlowError(navigate, "settings", setFlow))
				.catch(onFlowError);
			return;
		}

		// Otherwise we initialize it
		kratos
			.createBrowserSettingsFlow({
				returnTo: returnTo ? String(returnTo) : undefined,
			})
			.then(({ data }) => {
				setFlow(data);
			})
			.catch(handleFlowError(navigate, "settings", setFlow))
			.catch(onFlowError);
	}, [session, flowId, returnTo, flow, navigate]);

	useEffect(() => {
		setMethod(searchParams.get("method") ?? methodAll);
	}, [searchParams]);

	/**
	 * biome-ignore lint: react-hooks/exhaustive-deps: if add openSnackBar to dependencies, will occur the following error
	 * Maximum update depth exceeded. This can happen when a component calls setState inside useEffect, but useEffect either doesn't have a dependency array, or one of the dependencies changes on every render.
	 * FIXME: fix design and correct dependencies
	 */
	useEffect(() => {
		const success = flow?.ui.messages?.find((m) => m.type === "success");
		if (success) {
			openSnackBar({ type: "success", text: success.text });
		}
	}, [flow]);

	if (!session) return <p>Unauthenticated</p>;

	if (registrationReturnTo && registrationReturnTo.expiresAt > Date.now()) {
		removeRegistrationReturnTo();
		window.location.href = registrationReturnTo.returnTo;
	} else {
		removeRegistrationReturnTo();
	}

	if (!flow) return <Loading />;

	const submit = (values: UpdateSettingsFlowBody) => {
		return kratos
			.updateSettingsFlow({
				flow: String(flow?.id),
				updateSettingsFlowBody: values,
			})
			.then(({ data }) => {
				if (flow?.return_to) {
					window.location.href = flow?.return_to;
					return;
				}
				setFlow(data);
				if (data.state === "success") {
					if (mfaMethods.includes(method)) {
						return navigate(`/settings?method=${method}`);
					}
					return navigate("/settings");
				}
			})
			.catch(handleFlowError(navigate, "settings", setFlow))
			.catch((err: AxiosError<SettingsFlow>) => {
				// If the previous handler did not catch the error it's most likely a form validation error
				if (err.response?.status === 400) {
					// Yup, it is!
					setFlow(err.response?.data);
					return;
				}

				createFlowErrorHandler(openSnackBar, openModal, closeModal)(err);
			});
	};

	const openConfirmModal = (
		values: UpdateSettingsFlowBody,
		title: string,
		message: string,
		actionButtonText: string,
	) => {
		openModal({
			type: "warning",
			title,
			children: (
				<>
					<ModalContent>
						<div className="text-center">
							<p className="font-semibold text-xl mb-8">ARE YOU SURE ?</p>
							<p className="font-normal">{message}</p>
						</div>
					</ModalContent>
					<ModalAction>
						<button
							type="button"
							onClick={() => {
								closeModal();
								return submit(values);
							}}
							className="btn btn-lg short btn-t4-danger rounded-full"
						>
							{actionButtonText}
						</button>
						<button
							type="button"
							onClick={closeModal}
							className="btn btn-lg short btn-t4-border rounded-full"
						>
							Cancel
						</button>
					</ModalAction>
				</>
			),
		});
	};

	const onSubmit = (values: UpdateSettingsFlowBody) => {
		if ("webauthn_remove" in values && values.webauthn_remove) {
			openConfirmModal(
				values,
				"REMOVE SECURITY KEY ?",
				"You will remove a security key.",
				"REMOVE",
			);
			return Promise.resolve();
		}
		if ("totp_unlink" in values && values.totp_unlink) {
			openConfirmModal(
				values,
				"RESET AUTHENTICATION APP ?",
				"You will reset authentication app.",
				"RESET",
			);
			return Promise.resolve();
		}
		if ("lookup_secret_disable" in values && values.lookup_secret_disable) {
			openConfirmModal(
				values,
				"RESET RECOVERY CODE ?",
				"You will reset recovery code.",
				"RESET",
			);
			return Promise.resolve();
		}
		return submit(values);
	};

	if (mfaMethods.includes(method)) {
		return (
			<MfaSettings
				flow={flow}
				onSubmit={onSubmit}
				only={method}
				setMethod={setMethod}
			/>
		);
	}

	if (["profile", "password"].includes(method)) {
		return (
			<>
				<title>Settings | TIER IV Account</title>
				<DefaultLayout>
					<ContentContainerWrapper>
						<ContentContainer size="lg">
							{method === "profile" && (
								<ProfileSettings
									flow={flow}
									onSubmit={onSubmit}
									disabled={!!session.account.principal_group_id}
								/>
							)}
							{method === "password" && (
								<PasswordSettings flow={flow} onSubmit={onSubmit} />
							)}
							<p className="mt-20 text-center">
								<button
									type="button"
									className="t4-link"
									onClick={() => setMethod("all")}
								>
									Show all account settings
									<span className="font-semibold">?</span>
								</button>
							</p>
						</ContentContainer>
					</ContentContainerWrapper>
				</DefaultLayout>
			</>
		);
	}

	return (
		<>
			<title>Settings | TIER IV Account</title>
			<DefaultLayout>
				<ContentContainerWrapper>
					<ContentContainer size="lg">
						<ProfileSettings
							flow={flow}
							onSubmit={onSubmit}
							disabled={!!session.account.principal_group_id}
						/>
						<ReactivateEmailDeliveryStatusSettings />
						<PasswordSettings flow={flow} onSubmit={onSubmit} />
						<section className="mt-12">
							<h2 className="font-normal text-xl">
								MULTI-FACTOR AUTHENTICATION (MFA/2FA)
							</h2>
							<div className="w-full inline-block text-center mt-8">
								<Link
									to="/settings?method=mfa"
									className="btn btn-lg btn-t4-primary rounded-full"
								>
									MANAGE MFA
								</Link>
							</div>
						</section>
						{!session.account.principal_group_id && (
							<p className="mt-20 text-center">
								<Link to="/account/delete">
									DELETE ACCOUNT<span className="font-semibold">?</span>
								</Link>
							</p>
						)}
					</ContentContainer>
				</ContentContainerWrapper>
			</DefaultLayout>
		</>
	);
};
